import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

/**
 * Platform Admin login - superuser only.
 * Redirects to /platform/admin/dashboard on success.
 */
const PlatformAdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login({ email, password });
      if (!response.user?.is_superuser) {
        await logout();
        setError('Access denied. Only platform superusers can access.');
        setLoading(false);
        return;
      }
      navigate('/platform/admin/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-app flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-amber-500/90 rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-fg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white">Platform Admin</h1>
          <p className="mt-2 text-fg-muted">LaunchPoint System Control Center</p>
        </div>

        <div className="bg-app border border-border rounded-xl p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-fg-muted mb-2">Email</label>
              <input
                id="email"
                type="email"
                required
                className="block w-full px-4 py-3 bg-surface-muted border border-border rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-500"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-fg-muted mb-2">Password</label>
              <input
                id="password"
                type="password"
                required
                className="block w-full px-4 py-3 bg-surface-muted border border-border rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && (
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg bg-amber-500 text-fg font-medium hover:bg-amber-600 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
          <div className="mt-6 pt-6 border-t border-border">
            <a href="/" className="block text-center text-fg-muted hover:text-white text-sm">
              ← Return to LaunchPoint
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformAdminLogin;
